import { MonitorIcon } from "lucide-react";
import { useState } from "react";

import { useAccountSecurity, type AccountSession } from "../../state/accountSecurity";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { SettingsPageContainer, SettingsRow, SettingsSection } from "./settingsLayout";
import { searchableSetting } from "./settingsSearch";
import { AnalyticsPrivacyRow } from "./AnalyticsPrivacyRow";
import { BrowserLoginsSection } from "./BrowserLoginsSection";

function formatWhen(value: string | null): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function SessionRow({
  session,
  isBusy,
  onRevoke,
}: {
  session: AccountSession;
  isBusy: boolean;
  onRevoke: () => void;
}) {
  const started = formatWhen(session.createdAt);
  const isActive = session.status === "active";

  return (
    <div className="flex items-center gap-3 rounded-lg border border-border/60 px-3 py-2.5">
      <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground">
        <MonitorIcon className="size-4" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-foreground">{session.device}</p>
        <p className="truncate text-xs text-muted-foreground">
          {[session.ipAddress, session.authMethod, started].filter(Boolean).join(" · ")}
        </p>
      </div>
      {isActive ? null : <Badge variant="outline">Ended</Badge>}
      {isActive ? (
        <Button size="sm" variant="ghost" disabled={isBusy} onClick={onRevoke}>
          {isBusy ? "Ending…" : "Sign out"}
        </Button>
      ) : null}
    </div>
  );
}

function PrivacySection() {
  return (
    <SettingsSection id="privacy" title="Privacy">
      <AnalyticsPrivacyRow />
    </SettingsSection>
  );
}

/**
 * `/settings/security` — where this account is signed in, from WorkOS (the
 * identity provider), with the ability to end any of those sign-ins.
 *
 * No passkey row: WorkOS enrols passkeys only inside AuthKit's hosted
 * sign-in (no headless API, no add-a-passkey screen), so the only "set up"
 * this page could offer was signing the person out and hoping the hosted
 * page prompted — it did not, and it cost them their session.
 */
export function SecuritySettingsPanel() {
  const { security, unavailableMessage, isPending, revokeSession } = useAccountSecurity();
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (!security) {
    return (
      <SettingsPageContainer>
        <SettingsSection id="security" title="Security">
          <p className="px-3 py-3 text-sm text-muted-foreground sm:px-4">
            {isPending ? "Loading your sign-ins…" : (unavailableMessage ?? "")}
          </p>
        </SettingsSection>
        <BrowserLoginsSection />
        <PrivacySection />
      </SettingsPageContainer>
    );
  }

  const active = security.sessions.filter((session) => session.status === "active");
  const past = security.sessions.filter((session) => session.status !== "active").slice(0, 5);

  const revoke = (session: AccountSession) => {
    setBusyId(session.id);
    setError(null);
    void revokeSession(session.id).then((failure) => {
      setBusyId(null);
      setError(failure);
    });
  };

  return (
    <SettingsPageContainer>
      <SettingsSection id="security" title="Security">
        <SettingsRow
          {...searchableSetting("active-sessions")}
          description={
            active.length === 0
              ? "No active sign-ins recorded for this account."
              : `Where this account is signed in, as recorded by Amoreg's identity provider. Signing out ends that session everywhere it is used.`
          }
        >
          <div className="space-y-1 pb-2">
            {active.map((session) => (
              <SessionRow
                key={session.id}
                session={session}
                isBusy={busyId === session.id}
                onRevoke={() => revoke(session)}
              />
            ))}
            {error ? (
              <p role="alert" className="px-1 pt-1 text-xs text-destructive">
                {error}
              </p>
            ) : null}
          </div>
        </SettingsRow>

        {past.length > 0 ? (
          <SettingsRow
            {...searchableSetting("past-sessions")}
            description="Recent sign-ins that have already ended or expired."
          >
            <div className="space-y-1 pb-2">
              {past.map((session) => (
                <SessionRow
                  key={session.id}
                  session={session}
                  isBusy={false}
                  onRevoke={() => undefined}
                />
              ))}
            </div>
          </SettingsRow>
        ) : null}
      </SettingsSection>
      <BrowserLoginsSection />
      <PrivacySection />
    </SettingsPageContainer>
  );
}
